/**
 * Tabbed product collections: switches panels using aria-controls so tab order
 * always matches the correct panel (index order can differ from Liquid block order).
 */
class TabbedProductCollectionsComponent extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const tabStrip = this.querySelector('.tabbed-product-collections__tabs-outer');
    const panelsRoot = this.querySelector('.tabbed-product-collections__panels');
    this.#tabs = /** @type {HTMLButtonElement[]} */ (
      tabStrip ? Array.from(tabStrip.querySelectorAll('[role="tab"]')) : []
    );
    this.#panels = panelsRoot ? Array.from(panelsRoot.querySelectorAll('[role="tabpanel"]')) : [];

    for (const panel of this.#panels) {
      const marker = panel.querySelector('[data-tab-label]');
      if (!marker) continue;
      const label = /** @type {HTMLElement} */ (marker).dataset.tabLabel;
      marker.remove();
      if (!label) continue;
      const tab = this.#tabs.find((t) => t.getAttribute('aria-controls') === panel.id);
      if (!tab) continue;
      const labelEl = tab.querySelector('.tabbed-product-collections__tab-label');
      if (labelEl) labelEl.textContent = label;
    }

    if (!this.#tabs.length) return;

    for (const [i, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.#activateByTab(tab), { signal });
      tab.addEventListener('keydown', (e) => this.#onTabKeydown(e, i), { signal });
    }

    const selected = this.#tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (selected) {
      this.#activateByTab(selected, false);
    } else if (this.#tabs[0]) {
      this.#activateByTab(this.#tabs[0], false);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} focusTab
   */
  #activateByTab(tab, focusTab = true) {
    const panelId = tab.getAttribute('aria-controls');
    const activePanel =
      panelId && document.getElementById(panelId) instanceof HTMLElement
        ? /** @type {HTMLElement} */ (document.getElementById(panelId))
        : null;

    if (!activePanel || !this.contains(activePanel)) {
      return;
    }

    for (const t of this.#tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.#panels) {
      if (panel === activePanel) {
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('hidden', '');
        panel.setAttribute('inert', '');
      }
    }

    if (focusTab) {
      tab.focus();
    }
  }

  /**
   * @param {KeyboardEvent} e
   * @param {number} index
   */
  #onTabKeydown(e, index) {
    const layout = this.dataset.mobileLayout === 'stack' ? 'vertical' : 'horizontal';
    const prevKey = layout === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = layout === 'vertical' ? 'ArrowDown' : 'ArrowRight';

    let next = index;
    if (e.key === prevKey) {
      e.preventDefault();
      next = index <= 0 ? this.#tabs.length - 1 : index - 1;
    } else if (e.key === nextKey) {
      e.preventDefault();
      next = index >= this.#tabs.length - 1 ? 0 : index + 1;
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = this.#tabs.length - 1;
    } else {
      return;
    }

    const tab = this.#tabs[next];
    if (tab) this.#activateByTab(tab, true);
  }
}

if (!customElements.get('tabbed-product-collections-component')) {
  customElements.define('tabbed-product-collections-component', TabbedProductCollectionsComponent);
}
